import {
  v2Colors, v2Breakpoints, v2ReadableFontFamily,
} from './styles/tokens';
import { warningIcon } from './styles/common';

export function reactSelectStyles({ error, valid } = {}) {
  const borderColor = () => {
    if (error) return v2Colors.danger;
    if (valid) return v2Colors.primary;
    return v2Colors.border;
  };

  const ringShadow = (focused) => {
    if (focused) return `0 0 0 3px ${error ? 'rgba(217, 45, 32, 0.18)' : 'rgba(52, 64, 84, 0.14)'}`;
    if (valid) return '0 0 0 3px rgba(0, 166, 81, 0.12)';
    if (error) return '0 0 0 3px rgba(217, 45, 32, 0.12)';
    return 'none';
  };

  return {
    control: (base, state) => ({
      ...base,
      position: 'relative',
      minHeight: 40,
      borderRadius: 12,
      borderWidth: valid || error ? 1.75 : 1.5,
      borderColor: state.isDisabled ? v2Colors.borderStrong : borderColor(),
      backgroundColor: state.isDisabled ? '#e4e7ec' : v2Colors.inputBackground,
      opacity: state.isDisabled ? 0.7 : 1,
      cursor: state.isDisabled ? 'not-allowed' : 'default',
      boxShadow: state.isDisabled ? 'none' : ringShadow(state.isFocused),
      fontFamily: v2ReadableFontFamily,
      fontSize: 15,
      [`@media (max-width: ${v2Breakpoints.tablet})`]: {
        fontSize: 16,
      },
      '&:hover': {
        borderColor: state.isDisabled
          ? v2Colors.borderStrong
          : (error || valid || state.isFocused) ? borderColor() : v2Colors.borderStrong,
      },
      // Invalid: warning sign takes the far-right slot — the same spot it
      // occupies on text/number/select/date fields. The chevron shifts
      // inboard (see dropdownIndicator padding below), left of the icon.
      ...(error && !state.isDisabled
        ? {
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            right: 12,
            width: 16,
            height: 16,
            transform: 'translateY(-50%)',
            backgroundImage: warningIcon,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '16px 16px',
            pointerEvents: 'none',
          },
        }
        : {}),
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '2px 16px',
      // No error-state bump: the dropdownIndicator is a flex sibling that
      // already widens to ~64px on error (see its padding below), which is
      // what pushes the value/placeholder clear of the warning icon. Adding
      // it here too double-counts and collapses the placeholder in the
      // narrow single column.
      paddingRight: 16,
    }),
    placeholder: (base) => ({
      ...base,
      color: v2Colors.textFaint,
      fontSize: 15,
      fontWeight: 600,
    }),
    singleValue: (base) => ({
      ...base,
      color: v2Colors.text,
      fontWeight: 400,
    }),
    input: (base, state) => ({
      ...base,
      color: v2Colors.text,
      fontFamily: 'inherit',
      // Once an option is picked, react-select shows it as an absolutely
      // positioned label and shrinks the real <input> to a sliver at the far
      // left, leaving the text caret blinking over the start of the selected
      // word. Hide it until the user actually starts typing a new search.
      '& input': {
        caretColor: state.hasValue && !state.selectProps.inputValue ? 'transparent' : 'auto',
      },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
      ...base,
      color: v2Colors.textFaint,
      // Extra right padding on error shifts the chevron inboard so the
      // warning icon can sit at the far-right edge, right of the chevron.
      padding: error ? '0 34px 0 10px' : '0 10px',
      '&:hover': { color: v2Colors.textMuted },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: v2Colors.textFaint,
      padding: '0 6px',
      '&:hover': { color: v2Colors.danger },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 10,
      overflow: 'hidden',
      border: `1px solid ${v2Colors.border}`,
      boxShadow: '0 6px 20px rgba(16, 24, 40, 0.12)',
      fontFamily: v2ReadableFontFamily,
      fontSize: 15,
      zIndex: 20,
    }),
    menuList: (base) => ({
      ...base,
      padding: 4,
    }),
    option: (base, state) => ({
      ...base,
      borderRadius: 8,
      padding: '9px 16px',
      color: v2Colors.text,
      backgroundColor: state.isSelected
        ? v2Colors.primarySoft
        : state.isFocused
          ? v2Colors.surface
          : 'transparent',
      cursor: 'pointer',
      fontWeight: state.isSelected ? 600 : 400,
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: v2Colors.textMuted,
      fontSize: 14,
    }),
    loadingMessage: (base) => ({
      ...base,
      color: v2Colors.textMuted,
      fontSize: 14,
    }),
  };
}
